import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { getColDefs } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/SupCompAmounts/helpers/colDefinitions';
import { SupCompHoursLine } from 'routes/HumanResourcesModule/business/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { PayStateT } from 'routes/HumanResourcesModule/PayState/helpers/types';
import { useGetTableSupCompIndicators } from '../../helpers/hooks/payStateIndicators';
import Loading from 'components/Loading';
import PayStateBanner from '../../components/PayStateBanner';
import { formatSupCompTableIndicatorsForBanner } from '../helpers/dataHelpers';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';

type Props = {
    payState: PayStateT;
    month: number;
    cardHeaderActions: React.ReactNode;
    hsHcAmounts: SupCompHoursLine[];
    isLoading: boolean;
    showLastYearColumns: boolean;
};

const SupCompAmountsGrid = ({
    payState,
    month,
    cardHeaderActions,
    hsHcAmounts,
    isLoading,
    showLastYearColumns,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { data: tableIndicators, isLoading: indicatorsLoading } =
        useGetTableSupCompIndicators(payState.id);
    const fmtTableIndicators =
        formatSupCompTableIndicatorsForBanner(tableIndicators);

    const columnDefs = getColDefs({
        exercice: payState.exercice,
        month,
        showLastYearColumns,
        t,
    });

    const exportFileName = t('humanResources.payState.hsHcExportFileName', {
        dateVentilation: t(`humanResources.${payState.exercice}`).toLowerCase(),
    });

    const bannerComponent = ({ children }: { children: React.ReactElement }) =>
        indicatorsLoading ? (
            <Loading />
        ) : (
            <PayStateBanner
                title={t(`humanResources.payState.indicators.ev`)}
                exercice={payState.exercice}
                data={fmtTableIndicators}
            >
                {children}
            </PayStateBanner>
        );

    return (
        <StyHrDataGrid
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .supCompGrid,
            }}
            title={t('humanResources.hsHcAmounts')}
            rowData={hsHcAmounts}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isLoading}
            cardHeaderActions={cardHeaderActions}
            wrapperComponent={bannerComponent}
        />
    );
};

export default memo(SupCompAmountsGrid);
